import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Globe, Menu, X, Shield, Clock, Focus, Users, BookOpen, Lock } from 'lucide-react';

const CodeOfConduct = () => {
  const [language, setLanguage] = useState<'pl' | 'en'>('pl');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const content = {
    pl: {
      title: '🚀 IB ACCELERATOR: Regulamin dla Uczestników',
      intro: 'Witaj w programie IB Accelerator! Aby każda minuta kursu przybliżała Cię do wymarzonej "7", wspólnie dbamy o poniższe standardy:',
      rules: [
        {
          icon: Clock,
          title: 'Punktualność (Be on time)',
          description: 'Szanujemy swój czas. Zaczynamy punktualnie, aby w pełni wykorzystać potencjał każdej sesji.'
        },
        {
          icon: Focus,
          title: 'Pełne zaangażowanie (Phones away, minds on)',
          description: 'Na czas powtórek odkładamy telefony. Tworzymy przestrzeń skupienia (deep work), w której działamy tu i teraz.'
        },
        {
          icon: Users,
          title: 'Współpraca i komunikacja (Collaborate & Connect)',
          description: 'Budujemy relacje. Bądź otwarty na dialog z ekspertami i współpracę z innymi uczestnikami. Twoje pytania i aktywność wzbogacają całą grupę.'
        },
        {
          icon: BookOpen,
          title: 'Rzetelne przygotowanie (Proper Preparation)',
          description: 'Przychodzimy przygotowani i gotowi do pracy. Efektywność kursu zależy od Twojego zaangażowania.'
        },
        {
          icon: Shield,
          title: 'Szacunek i Prywatność (Respect & Privacy)',
          description: 'Szanujemy wiedzę ekspertów i komfort innych uczniów. Podczas trwania kursu nie publikujemy materiałów ani przebiegu zajęć w mediach społecznościowych – skupiamy się na nauce, a nie na relacjonowaniu.'
        },
        {
          icon: Lock,
          title: 'Wyłączność materiałów (Exclusive Content)',
          description: 'Otrzymujesz dostęp do autorskich, zastrzeżonych materiałów. Są one dedykowane wyłącznie dla uczestników kursu. Obowiązuje całkowity zakaz kopiowania i udostępniania ich osobom trzecim.'
        }
      ]
    },
    en: {
      title: '🚀 IB ACCELERATOR: Student\'s Code of Conduct',
      intro: 'Welcome to the IB Accelerator program! To ensure every minute brings you closer to your dream "7", we maintain these standards together:',
      rules: [
        {
          icon: Clock,
          title: 'Punctuality',
          description: 'Respect everyone\'s time. We start promptly to maximize every learning minute.'
        },
        {
          icon: Focus,
          title: 'Focus',
          description: 'Phones away, minds on. We create a "deep work" environment to ensure effective revision.'
        },
        {
          icon: Users,
          title: 'Collaboration & Communication',
          description: 'Be active and open. Engage with experts and peers—education is built through relationships.'
        },
        {
          icon: BookOpen,
          title: 'Preparation',
          description: 'Come ready to work. Your dedication is the key to achieving the "Road to 7" goal.'
        },
        {
          icon: Shield,
          title: 'Respect & Privacy',
          description: 'Respect the experts and your fellow students. No social media posting during sessions—let\'s stay present and focused.'
        },
        {
          icon: Lock,
          title: 'Exclusivity of Materials',
          description: 'All resources provided are proprietary and for participants only. Copying or sharing them with third parties is strictly prohibited.'
        }
      ],
      closing: 'We work with passion and precision. Your journey to excellence starts now!'
    }
  };

  const t = content[language];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation */}
      <nav className="bg-white shadow-sm border-b">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="text-2xl font-bold text-blue-600">
              IB Accelerator
            </Link>
            
            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              <Link to="/" className="text-gray-600 hover:text-blue-600 transition-colors">
                {language === 'pl' ? 'Strona główna' : 'Home'}
              </Link>
              <Link to="/experts" className="text-gray-600 hover:text-blue-600 transition-colors">
                {language === 'pl' ? 'Eksperci' : 'Experts'}
              </Link>
              <Link to="/schedule" className="text-gray-600 hover:text-blue-600 transition-colors">
                {language === 'pl' ? 'Harmonogram' : 'Schedule'}
              </Link>
              <Link to="/topics" className="text-gray-600 hover:text-blue-600 transition-colors">
                {language === 'pl' ? 'Tematy' : 'Topics'}
              </Link>
              <Link to="/pricing" className="text-gray-600 hover:text-blue-600 transition-colors">
                {language === 'pl' ? 'Dołącz do kursu' : 'Join the DP Accelerator'}
              </Link>
              <Link to="/code-of-conduct" className="text-blue-600 font-semibold">
                {language === 'pl' ? 'Regulamin' : 'Code of Conduct'}
              </Link>
              <Link to="/contact" className="text-gray-600 hover:text-blue-600 transition-colors">
                {language === 'pl' ? 'Kontakt' : 'Contact'}
              </Link>
              
              <Button
                variant="outline"
                size="sm"
                onClick={() => setLanguage(language === 'pl' ? 'en' : 'pl')}
                className="flex items-center space-x-2"
              >
                <Globe className="w-4 h-4" />
                <span>{language === 'pl' ? 'EN' : 'PL'}</span>
              </Button>
            </div>

            {/* Mobile menu button */}
            <Button
              variant="outline"
              size="sm"
              className="md:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
            </Button>
          </div>

          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <div className="md:hidden bg-white border-b shadow-lg">
              <div className="container mx-auto px-4 py-4 space-y-4">
                <Link 
                  to="/" 
                  className="block text-gray-600 hover:text-blue-600 transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {language === 'pl' ? 'Strona główna' : 'Home'}
                </Link>
                <Link 
                  to="/experts" 
                  className="block text-gray-600 hover:text-blue-600 transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {language === 'pl' ? 'Eksperci' : 'Experts'}
                </Link>
                <Link 
                  to="/schedule" 
                  className="block text-gray-600 hover:text-blue-600 transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {language === 'pl' ? 'Harmonogram' : 'Schedule'}
                </Link>
                <Link 
                  to="/topics" 
                  className="block text-gray-600 hover:text-blue-600 transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {language === 'pl' ? 'Tematy' : 'Topics'}
                </Link>
                <Link 
                  to="/pricing" 
                  className="block text-gray-600 hover:text-blue-600 transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {language === 'pl' ? 'Dołącz do kursu' : 'Join the DP Accelerator'}
                </Link>
                <Link 
                  to="/code-of-conduct" 
                  className="block text-blue-600 font-semibold"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {language === 'pl' ? 'Regulamin' : 'Code of Conduct'}
                </Link>
                <Link 
                  to="/contact" 
                  className="block text-gray-600 hover:text-blue-600 transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {language === 'pl' ? 'Kontakt' : 'Contact'}
                </Link>
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setLanguage(language === 'pl' ? 'en' : 'pl');
                    setMobileMenuOpen(false);
                  }}
                  className="flex items-center space-x-2 w-full justify-center"
                >
                  <Globe className="w-4 h-4" />
                  <span>{language === 'pl' ? 'EN' : 'PL'}</span>
                </Button>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-blue-600 to-purple-700 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              {t.title}
            </h1>
            <p className="text-xl mb-8 text-blue-100">
              {t.intro}
            </p>
          </div>
        </div>
      </section>

      {/* Code of Conduct Rules */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="grid gap-8">
              {t.rules.map((rule, index) => {
                const IconComponent = rule.icon;
                return (
                  <Card key={index} className="hover:shadow-lg transition-shadow">
                    <CardHeader>
                      <div className="flex items-center space-x-4">
                        <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                          <IconComponent className="w-6 h-6 text-blue-600" />
                        </div>
                        <CardTitle className="text-xl text-gray-900">
                          {rule.title}
                        </CardTitle>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <p className="text-gray-600 leading-relaxed">
                        {rule.description}
                      </p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Closing Message for English */}
            {language === 'en' && (
              <Card className="mt-8 bg-gradient-to-r from-blue-50 to-purple-50 border-blue-200">
                <CardContent className="p-8 text-center">
                  <p className="text-lg font-semibold text-blue-800">
                    {t.closing}
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default CodeOfConduct;